<template>
  <div ref="editorContainer" class="code-editor-container"></div>
</template>

<script>
import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { EditorView, basicSetup } from 'codemirror'
import { json } from '@codemirror/lang-json'
import { EditorState } from '@codemirror/state'

export default defineComponent({
  props: {
    modelValue: String,
    defaultValue: String,
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const editorContainer = ref(null)
    let view

    const createEditor = () => {
      const state = EditorState.create({
        doc: props.modelValue || props.defaultValue || '',
        extensions: [
          basicSetup,
          json(),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              emit('update:modelValue', update.state.doc.toString())
            }
          }),
          EditorView.editable.of(!props.readOnly),
          EditorState.readOnly.of(props.readOnly),
          EditorView.theme({
            '&': { height: '100%' },
            '.cm-scroller': { overflow: 'auto' }
          })
        ]
      })

      view = new EditorView({
        state,
        parent: editorContainer.value
      })
    }

    watch(() => props.modelValue, (newValue) => {
      if (view && newValue !== view.state.doc.toString()) {
        view.dispatch({
          changes: { from: 0, to: view.state.doc.length, insert: newValue || '' }
        })
      }
    })

    watch(() => props.readOnly, (newReadOnly) => {
      // Re-creating or re-configuring state is cleaner for read-only changes in CM6
      if (view) {
        view.destroy()
        createEditor()
      }
    })

    onMounted(() => {
      createEditor()
    })

    onBeforeUnmount(() => {
      if (view) {
        view.destroy()
      }
    })

    return { editorContainer }
  }
})
</script>

<style scoped>
.code-editor-container {
  border: 1px solid #ddd;
  height: 600px;
  text-align: left;
}
:deep(.cm-editor) {
  height: 100%;
}
</style>
